import { useState, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import {
  getStudentExits,
  createStudentExit,
  updateStudentExit,
  deleteStudentExit,
  getStudentExitById,
  approveStudentExit,
  rejectStudentExit,
  completeStudentExit,
  getDailySchedulePeriods,
} from "../../../../services/studentExitService";
import {
  StudentExit,
  StudentExitCreatePayload,
  StudentExitUpdatePayload,
  StudentExitQueryParams,
} from "../../../../types/studentExitTypes";
import { getGuardianByStudentId } from "services/guardianService";
import { getAuthorizedPickUpPersonsByStudentId } from "services/authorizedPickUpPersonService";
import { getStudents } from "services/studentService";

export const useStudentExit = () => {
  const { t } = useTranslation();

  const [studentExits, setStudentExits] = useState<StudentExit[]>([]);
  const [pagination, setPagination] = useState<any>({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedStudentExit, setSelectedStudentExit] = useState<StudentExit | null>(null);


  const statusOptions = [
    { label: t("studentExit.status.pending"), value: "pending" },
    { label: t("studentExit.status.approved"), value: "approved" },
    { label: t("studentExit.status.rejected"), value: "rejected" },
    { label: t("studentExit.status.completed"), value: "completed" },
  ];

  const exitTypeOptions = [
    { label: t("studentExit.exitType.morning"), value: "morning" },
    { label: t("studentExit.exitType.afternoon"), value: "afternoon" },
    { label: t("studentExit.exitType.early"), value: "early" },
  ];

  const fetchStudentExits = async (filters: StudentExitQueryParams) => {
    setLoading(true);
    try {
      const response = await getStudentExits(filters);
      setStudentExits(response.data);
      setPagination(response.pagination);
      setError(null);
    } catch (err) {
      console.error("Error fetching student exits:", err);
      // setError(t("studentExit.error.fetch"));
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await getStudents({ page: 1, limit: 100000 });
      return response.data || [];
    } catch (err) {
      console.error("Error fetching students:", err);
      return [];
    }
  };

  const handleCreate = async (payload: StudentExitCreatePayload) => {
    try {
      await createStudentExit(payload);
      message.success(t("studentExit.success_create"));
      return true;
    } catch (err) {
      console.error("Error creating student exit:", err);
      return false;
    }
  };

  const handleUpdate = async (studentExitId: string, payload: StudentExitUpdatePayload) => {
    try {
      await updateStudentExit(studentExitId, payload);
      message.success(t("studentExit.success_update"));
      return true;
    } catch (err) {
      console.error("Error updating student exit:", err);
      return false;
    }
  };

  const handleApprove = async (studentExitId: string, notes?: string) => {
    try {
      await approveStudentExit(studentExitId, notes);
      message.success(t("studentExit.success_approve"));
      return true;
    } catch (err) {
      console.error("Error approving student exit:", err);
      return false;
    }
  };

  const handleReject = async (studentExitId: string, notes?: string) => {
    try {
      await rejectStudentExit(studentExitId, notes);
      message.success(t("studentExit.success_reject"));
      return true;
    } catch (err) {
      console.error("Error rejecting student exit:", err);
      return false;
    }
  };

  const handleComplete = async (studentExitId: string) => {
    try {
      await completeStudentExit(studentExitId);
      message.success(t("studentExit.success_complete"));
      return true;
    } catch (err) {
      console.error("Error completing student exit:", err);
      return false;
    }
  };

  const handleDelete = async (studentExitId: string) => {
    try {
      await deleteStudentExit(studentExitId);
      message.success(t("studentExit.success_delete"));
      return true;
    } catch (err) {
      console.error("Error deleting student exit:", err);
      return false;
    }
  };

  const handleGetById = async (studentExitId: string) => {
    try {
      const response = await getStudentExitById(studentExitId);
      return response.data;
    } catch (err) {
      console.error("Error fetching student exit by ID:", err);
      return null;
    }
  };

  const handleGetGuardianByStudentId = async (studentId: string) => {
    try {
      const response = await getGuardianByStudentId(studentId);
      return response.data;
    } catch (err) {
      console.error("Error fetching guardian by student ID:", err);
      return null;
    }
  };

  const handleGetAuthorizedByStudentId = async (studentId: string) => {
    try {
      const response = await getAuthorizedPickUpPersonsByStudentId(studentId);
      return response.data;
    } catch (err) {
      console.error("Error fetching authorized persons by student ID:", err);
      return null;
    }
  };

  const handleGetDailySchedulePeriods = async (studentExitId: string) => {
    try {
      const response = await getDailySchedulePeriods(studentExitId);
      return response.data;
    } catch (err) {
      console.error("Error fetching daily schedule periods by student exit ID:", err);
      return null;
    }
  };


  return {
    studentExits,
    infoPagination: {
      total: pagination?.total,
      page: pagination?.page,
      limit: pagination?.limit,
    },
    loading,
    error,
    selectedStudentExit,
    setSelectedStudentExit,
    fetchStudentExits,
    handleCreate,
    handleUpdate,
    handleApprove,
    handleReject,
    handleComplete,
    handleDelete,
    handleGetById,
    statusOptions,
    exitTypeOptions,
    handleGetGuardianByStudentId,
    handleGetAuthorizedByStudentId,
    fetchStudents,
    handleGetDailySchedulePeriods,
  };
};

export default useStudentExit;